/**
 * Signup Screen
 * Create new shop and optional admin user
 *
 * Enterprise Features:
 * - Dynamic country code dropdown from configuration API
 * - Dynamic shop type dropdown from configuration API
 * - Cached configuration data (5-minute expiry)
 * - Form validation with comprehensive error handling
 * - Mobile-first responsive design
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Alert,
  TouchableOpacity,
  Modal,
  FlatList,
  ActivityIndicator,
  Image,
  Linking,
} from 'react-native';
import {
  useAuthStore,
  configurationService,
  policyService,
  getConfig,
  generateOtp,
  validateOtp,
  validateCaptcha,
  type Country,
  type ShopType,
  type Policy,
} from '@app/core';
import { Button, Input, Card, Text, TurnstileCaptcha } from '@app/design-system';
import { colors } from '@app/design-system';

interface SignupScreenProps {
  onSignupSuccess?: () => void;
  onNavigateToLogin?: () => void;
  onViewTerms?: () => void;
  onViewPrivacy?: () => void;
  onViewGrievance?: () => void;
}

interface SignupForm {
  shopName: string;
  ownerName: string;
  email: string;
  mobileCountryCode: string;
  mobileNumber: string;
  shopType: string;
  address: string;
  gpsLocation: {
    type: string;
    coordinates: [number, number];
  } | null;
  password: string;
  confirmPassword: string;
  partnerId: string; // Optional partner ID
}

interface SignupErrors {
  shopName?: string;
  ownerName?: string;
  email?: string;
  mobileNumber?: string;
  shopType?: string;
  password?: string;
  confirmPassword?: string;
}

export const SignupScreen: React.FC<SignupScreenProps> = ({
  onSignupSuccess,
  onNavigateToLogin,
  onViewTerms,
  onViewPrivacy,
  onViewGrievance,
}) => {
  // Policy viewer modal state
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [viewingPolicy, setViewingPolicy] = useState<Policy | null>(null);
  const [policyPdfUrl, setPolicyPdfUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const [form, setForm] = useState<SignupForm>({
    shopName: '',
    ownerName: '',
    email: '',
    mobileCountryCode: '91',
    mobileNumber: '',
    shopType: '',
    address: '',
    gpsLocation: null,
    password: '',
    confirmPassword: '',
    partnerId: '', // Optional partner ID
  });

  const [errors, setErrors] = useState<SignupErrors>({});
  const { signup, isLoading, error, tokens } = useAuthStore();
  const [loadingLocation, setLoadingLocation] = useState(false);

  // Mobile Verification States
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(true); // Show captcha on page load
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaVerified, setCaptchaVerified] = useState(false); // Track if captcha is verified
  const [captchaValidating, setCaptchaValidating] = useState(false); // Track captcha validation loading
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [emailAlreadyRegistered, setEmailAlreadyRegistered] = useState(false);
  const [mobileAlreadyRegistered, setMobileAlreadyRegistered] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // Logo URL - use relative path
  const logoUrl = '/assets/icons/Bazly_Logo.jpg';

  // Policy acceptance states
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptGrievance, setAcceptGrievance] = useState(false);

  // Policy data states
  const [termsPolicy, setTermsPolicy] = useState<Policy | null>(null);
  const [privacyPolicy, setPrivacyPolicy] = useState<Policy | null>(null);
  const [grievancePolicy, setGrievancePolicy] = useState<Policy | null>(null);
  const [policiesLoading, setPoliciesLoading] = useState(true);

  // Configuration data states
  const [countries, setCountries] = useState<Country[]>([]);
  const [shopTypes, setShopTypes] = useState<ShopType[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  // Modal states
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showShopTypeModal, setShowShopTypeModal] = useState(false);

  /**
   * Load configuration data and policies on component mount
   * Uses cached data if available (5-minute cache)
   */
  useEffect(() => {
    loadConfigurations();
    loadPolicies();
  }, []);

  /**
   * Load latest policies from API
   */
  const loadPolicies = async (): Promise<void> => {
    try {
      setPoliciesLoading(true);
      console.log('📋 [SignupScreen] Loading policies...');

      const [terms, privacy, grievance] = await Promise.all([
        policyService.getLatestPolicy('TERMS'),
        policyService.getLatestPolicy('PRIVACY'),
        policyService.getLatestPolicy('GRIEVANCE'),
      ]);

      setTermsPolicy(terms);
      setPrivacyPolicy(privacy);
      setGrievancePolicy(grievance);

      console.log('✅ [SignupScreen] Policies loaded', {
        hasTerms: !!terms,
        hasPrivacy: !!privacy,
        hasGrievance: !!grievance,
      });
    } catch (err) {
      console.error('❌ [SignupScreen] Failed to load policies:', err);
      // Don't block signup if policies fail to load
    } finally {
      setPoliciesLoading(false);
    }
  };

  const loadConfigurations = async (): Promise<void> => {
    try {
      setLoadingConfigs(true);
      setConfigError(null);

      console.log('📡 [SignupScreen] Loading configuration data...');

      // Fetch both in parallel (uses cache if available)
      const [countriesData, shopTypesData] = await Promise.all([
        configurationService.getCountries({ activeOnly: true }),
        configurationService.getShopTypes({ activeOnly: true }),
      ]);

      console.log(`✅ [SignupScreen] Loaded ${countriesData.length} countries`);
      console.log(`✅ [SignupScreen] Loaded ${shopTypesData.length} shop types`);

      setCountries(countriesData);
      setShopTypes(shopTypesData);

      // Set default values if not already set
      if (countriesData.length > 0 && !form.mobileCountryCode) {
        const defaultCountry = countriesData.find(c => c.countryCode === 'IND') || countriesData[0];
        setForm((prev) => ({
          ...prev,
          mobileCountryCode: defaultCountry.mobileCountryCode,
        }));
      }

      // Don't auto-select shop type - force user to choose
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load configuration data';
      console.error('❌ [SignupScreen] Configuration load error:', errorMessage);
      setConfigError(errorMessage);
      Alert.alert('Error', 'Failed to load configuration data. Please try again.');
    } finally {
      setLoadingConfigs(false);
    }
  };

  const updateForm = (field: keyof SignupForm, value: string): void => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field as keyof SignupErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  /**
   * Check if all required form fields are filled
   */
  const isFormComplete = (): boolean => {
    return !!(
      form.shopName.trim() &&
      form.ownerName.trim() &&
      form.email.trim() &&
      form.mobileCountryCode.trim() &&
      form.mobileNumber.trim() &&
      form.shopType.trim() &&
      form.address.trim() &&
      // GPS location is optional - can be updated later via Manage Shop
      form.password.trim() &&
      form.confirmPassword.trim() &&
      isMobileVerified && // Mobile verification is mandatory
      acceptTerms &&
      acceptPrivacy &&
      acceptGrievance
    );
  };

  /**
   * Check if form is valid and can be submitted
   */
  const canSubmit = (): boolean => {
    return (
      isFormComplete() &&
      isMobileVerified && // Ensure mobile is verified
      acceptTerms &&
      acceptPrivacy &&
      acceptGrievance &&
      !isLoading
    );
  };

  const validateForm = (): boolean => {
    const newErrors: SignupErrors = {};

    if (!form.shopName.trim()) {
      newErrors.shopName = 'Shop name is required';
    }

    if (!form.ownerName.trim()) {
      newErrors.ownerName = 'Owner name is required';
    }

    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!form.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(form.mobileNumber)) {
      newErrors.mobileNumber = 'Mobile number must be 10 digits';
    }

    if (!form.shopType.trim()) {
      newErrors.shopType = 'Shop type is required';
    }

    if (!form.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (form.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle mobile number change
   * Reset verification states when mobile number changes
   */
  const handleMobileNumberChange = (value: string) => {
    console.log('📱 [SignupScreen] Mobile number input changed:', {
      oldValue: form.mobileNumber,
      newValue: value,
      length: value.length
    });

    // Only allow digits
    const digitsOnly = value.replace(/\D/g, '');
    console.log('📱 [SignupScreen] Digits only:', digitsOnly);

    updateForm('mobileNumber', digitsOnly);

    // Reset verification states when mobile number changes
    if (digitsOnly !== form.mobileNumber) {
      console.log('📱 [SignupScreen] Mobile number changed, resetting verification states');
      setIsMobileVerified(false);
      setOtpSent(false);
      setOtp('');
      setOtpError(null);
      setMobileAlreadyRegistered(false); // Reset mobile error state
    }
  };

  /**
   * Handle email change
   * Reset verification states when email changes
   */
  const handleEmailChange = (value: string) => {
    updateForm('email', value);

    // Reset verification states when email changes
    setIsMobileVerified(false);
    setOtpSent(false);
    setOtp('');
    setOtpError(null);
    setEmailAlreadyRegistered(false); // Reset email error state
    setMobileAlreadyRegistered(false); // Reset mobile error state
  };

  /**
   * Handle logo click to navigate to bazly.in
   */
  const handleLogoClick = () => {
    Linking.openURL('https://bazly.in');
  };

  /**
   * Handle captcha verification on page load with backend validation
   */
  const handleCaptchaVerify = async (token: string) => {
    console.log('🔐 [SignupScreen] ====== CAPTCHA VERIFICATION START ======');
    console.log('🔐 [SignupScreen] Captcha token received:', {
      tokenLength: token.length,
      tokenPreview: token.substring(0, 20) + '...',
      timestamp: new Date().toISOString()
    });

    try {
      // Set loading state and store token locally
      setCaptchaValidating(true);
      setCaptchaToken(token);

      console.log('🔐 [SignupScreen] Validating captcha with backend API...');

      // Call backend API to validate captcha token
      const validationResponse = await validateCaptcha({ token });

      console.log('🔐 [SignupScreen] Backend validation response:', {
        success: validationResponse.success,
        message: validationResponse.message,
        timestamp: new Date().toISOString()
      });

      if (validationResponse.success) {
        // Only set as verified if backend confirms validation
        setCaptchaVerified(true);

        console.log('✅ [SignupScreen] Captcha verified successfully by backend');
        console.log('✅ [SignupScreen] ====== CAPTCHA VERIFICATION SUCCESS ======');

        // Hide captcha after successful backend validation
        setTimeout(() => {
          setShowCaptcha(false);
        }, 500);

      } else {
        console.error('❌ [SignupScreen] Backend captcha validation failed:', validationResponse.message);
        Alert.alert(
          'Verification Failed',
          validationResponse.message || 'Captcha validation failed. Please try again.',
          [{
            text: 'Try Again',
            onPress: () => {
              // Reset captcha state to allow retry
              setCaptchaToken(null);
              setCaptchaVerified(false);
            }
          }]
        );
      }

    } catch (error: any) {
      console.error('❌ [SignupScreen] ====== CAPTCHA VALIDATION ERROR ======');
      console.error('❌ [SignupScreen] Captcha validation exception:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });

      const errorMessage = error.message || 'Failed to validate captcha. Please try again.';

      Alert.alert(
        'Verification Error',
        errorMessage,
        [{
          text: 'Try Again',
          onPress: () => {
            // Reset captcha state to allow retry
            setCaptchaToken(null);
            setCaptchaVerified(false);
          }
        }]
      );
    } finally {
      // Always clear loading state
      setCaptchaValidating(false);
    }
  };

  /**
   * Handle Send OTP button click
   */
  const handleSendOtp = async () => {
    console.log('📱 [SignupScreen] ====== SEND OTP START ======');

    // Validate email before sending OTP
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      console.error('❌ [SignupScreen] Invalid email, cannot generate OTP');
      Alert.alert('Error', 'Please enter a valid email address');
      return;
    }

    // Validate mobile number
    if (!form.mobileNumber || form.mobileNumber.length !== 10) {
      console.error('❌ [SignupScreen] Invalid mobile number, cannot generate OTP');
      Alert.alert('Error', 'Please enter a valid 10-digit mobile number');
      return;
    }

    // Check if captcha is verified
    if (!captchaVerified) {
      console.error('❌ [SignupScreen] Captcha not verified');
      Alert.alert('Error', 'Please verify you are human first');
      return;
    }

    try {
      setSendingOtp(true);
      setOtpError(null);

      // Build OTP request WITHOUT captchaToken
      const otpRequest = {
        email: form.email,
        countryCode: form.mobileCountryCode,
        mobileNumber: form.mobileNumber,
      };

      console.log('📱 [SignupScreen] OTP generation request payload:', {
        email: otpRequest.email,
        countryCode: otpRequest.countryCode,
        mobileNumber: otpRequest.mobileNumber.replace(/\d(?=\d{4})/g, '*'),
        timestamp: new Date().toISOString()
      });

      // 🚨 COMPLETE PAYLOAD LOGGING FOR API REQUEST
      console.log('📋 [SignupScreen] ====== COMPLETE API PAYLOAD ======');
      console.log('📋 [SignupScreen] Endpoint: POST /api/otp/generate');
      console.log('📋 [SignupScreen] Complete Request Object:');
      console.log('📋 [SignupScreen] Request Body (Full JSON):');
      console.log(JSON.stringify(otpRequest, null, 2));
      console.log('📋 [SignupScreen] Individual Fields:');
      console.log('  - email:', `"${otpRequest.email}"`);
      console.log('  - countryCode:', `"${otpRequest.countryCode}"`);
      console.log('  - mobileNumber:', `"${otpRequest.mobileNumber}"`);
      console.log('📋 [SignupScreen] NOTE: captchaToken removed from payload');
      console.log('📋 [SignupScreen] ====== END PAYLOAD ======');

      console.log('📡 [SignupScreen] Calling generateOtp API...');
      const response = await generateOtp(otpRequest as any);

      console.log('📱 [SignupScreen] OTP API response:', {
        success: response.success,
        message: response.message,
        timestamp: new Date().toISOString()
      });

      if (response.success) {
        console.log('✅ [SignupScreen] OTP sent successfully, updating UI state');
        setOtpSent(true);
        Alert.alert('OTP Sent', response.message);
        console.log('✅ [SignupScreen] ====== SEND OTP SUCCESS ======');
      } else {
        console.error('❌ [SignupScreen] OTP generation failed:', response.message);
        setOtpError(response.message || 'Failed to send OTP');
        Alert.alert('Error', response.message || 'Failed to send OTP');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to send OTP. Please try again.';
      console.error('❌ [SignupScreen] OTP generation exception:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });

      // Handle specific error cases professionally
      if (errorMessage.toLowerCase().includes('already registered') ||
          errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('exist')) {

        // Check if it's an email or mobile number error
        const isEmailError = errorMessage.toLowerCase().includes('email');
        const isMobileError = errorMessage.toLowerCase().includes('mobile number') ||
                             errorMessage.toLowerCase().includes('mobile') && errorMessage.toLowerCase().includes('registered');

        if (isEmailError) {
          // Professional message for already registered email
          setEmailAlreadyRegistered(true);
          setOtpError(null);
          Alert.alert(
            'Email Already Registered',
            'This email address is already associated with an existing account. Please use a different email address to create your new shop account.\n\nAlternatively, if this is your email, you can try logging in instead.',
            [
              {
                text: 'Use Different Email',
                style: 'default',
                onPress: () => {
                  // Reset verification states to allow user to change email
                  setIsMobileVerified(false);
                  setOtpSent(false);
                  setOtp('');
                  setOtpError(null);
                }
              },
              {
                text: 'Go to Login',
                style: 'cancel',
                onPress: () => {
                  if (onNavigateToLogin) {
                    onNavigateToLogin();
                  }
                }
              }
            ]
          );
        } else if (isMobileError) {
          // Professional message for already registered mobile number
          setMobileAlreadyRegistered(true);
          setOtpError(null);
          Alert.alert(
            'Mobile Number Already Registered',
            'This mobile number is already associated with an existing account. Please use a different mobile number to create your new shop account.\n\nIf this is your mobile number, you can try logging in instead.',
            [
              {
                text: 'Use Different Number',
                style: 'default',
                onPress: () => {
                  // Reset verification states to allow user to change mobile number
                  setIsMobileVerified(false);
                  setOtpSent(false);
                  setOtp('');
                  setOtpError(null);
                  setEmailAlreadyRegistered(false);
                }
              },
              {
                text: 'Go to Login',
                style: 'cancel',
                onPress: () => {
                  if (onNavigateToLogin) {
                    onNavigateToLogin();
                  }
                }
              }
            ]
          );
        }
        // Generic error handling for other issues
        setOtpError(errorMessage);
        Alert.alert('Verification Error', errorMessage);
      }
    } finally {
      setSendingOtp(false);
      console.log('🔚 [SignupScreen] ====== SEND OTP END ======');
    }
  };

  /**
   * Handle OTP validation
   */
  const handleValidateOtp = async () => {
    console.log('🔐 [SignupScreen] ====== OTP VALIDATION START ======');
    console.log('🔐 [SignupScreen] OTP validation request:', {
      otpLength: otp.length,
      countryCode: form.mobileCountryCode,
      mobileNumber: form.mobileNumber.replace(/\d(?=\d{4})/g, '*'),
      timestamp: new Date().toISOString()
    });

    if (!otp || otp.length !== 6) {
      console.warn('⚠️ [SignupScreen] Invalid OTP length:', otp.length);
      setOtpError('Please enter 6-digit OTP');
      return;
    }

    try {
      setVerifyingOtp(true);
      setOtpError(null);

      console.log('📡 [SignupScreen] Calling validateOtp API...');
      const response = await validateOtp({
        countryCode: form.mobileCountryCode,
        mobileNumber: form.mobileNumber,
        otp: otp,
      });

      console.log('🔐 [SignupScreen] OTP validation response:', {
        success: response.success,
        message: response.message,
        timestamp: new Date().toISOString()
      });

      if (response.success) {
        console.log('✅ [SignupScreen] Mobile number verified successfully');
        setIsMobileVerified(true);
        setOtpError(null);
        Alert.alert('Success', 'Mobile number verified successfully! ✓');
        console.log('✅ [SignupScreen] ====== OTP VALIDATION SUCCESS ======');
      } else {
        console.error('❌ [SignupScreen] OTP validation failed:', response.message);
        setOtpError(response.message || 'Invalid OTP');
        Alert.alert('Error', response.message || 'Invalid OTP');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify OTP. Please try again.';
      console.error('❌ [SignupScreen] OTP validation exception:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      setOtpError(errorMessage);
      Alert.alert('Error', errorMessage);
    } finally {
      setVerifyingOtp(false);
      console.log('🔚 [SignupScreen] ====== OTP VALIDATION END ======');
    }
  };

  /**
   * Handle resend OTP
   */
  const handleResendOtp = async () => {
    // Validate that captcha is verified
    if (!captchaVerified) {
      Alert.alert('Error', 'Please complete captcha verification first');
      return;
    }

    try {
      setSendingOtp(true);
      setOtpError(null);
      setOtp('');

      // Build OTP request WITHOUT captchaToken
      const otpRequest = {
        email: form.email,
        countryCode: form.mobileCountryCode,
        mobileNumber: form.mobileNumber,
      };

      const response = await generateOtp(otpRequest as any);

      if (response.success) {
        console.log('✅ [SignupScreen] OTP resent successfully');
        Alert.alert('OTP Resent', response.message);
      } else {
        Alert.alert('Error', response.message || 'Failed to resend OTP');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to resend OTP';
      console.error('❌ [SignupScreen] OTP resend failed:', error);
      Alert.alert('Error', errorMessage);
    } finally {
      setSendingOtp(false);
    }
  };

  /**
   * Handle viewing a policy in modal
   */
  const handleViewPolicy = async (type: 'TERMS' | 'PRIVACY' | 'GRIEVANCE') => {
    try {
      setLoadingPdf(true);
      setShowPolicyModal(true);

      console.log(`[SignupScreen] Loading policy for viewing: ${type}`);

      const policy = type === 'TERMS' ? termsPolicy :
                     type === 'PRIVACY' ? privacyPolicy :
                     grievancePolicy;

      if (!policy) {
        Alert.alert('Error', 'Policy document not available');
        setShowPolicyModal(false);
        return;
      }

      setViewingPolicy(policy);

      // Fetch the PDF blob with public access token
      const response = await fetch(
        `${getConfig().apiBaseUrl}/api/policies/${policy.id}/document`,
        {
          headers: {
            // Use public token for policy documents
            'Authorization': `Bearer ${tokens?.publicAccessToken || tokens?.oauthToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPolicyPdfUrl(blobUrl);

      console.log(`[SignupScreen] PDF loaded successfully for ${type}`);
    } catch (error) {
      console.error('[SignupScreen] Failed to load PDF:', error);
      Alert.alert('Error', 'Failed to load policy document');
      setShowPolicyModal(false);
    } finally {
      setLoadingPdf(false);
    }
  };

  /**
   * Handle closing policy modal
   */
  const handleClosePolicyModal = () => {
    setShowPolicyModal(false);
    setViewingPolicy(null);
    if (policyPdfUrl) {
      URL.revokeObjectURL(policyPdfUrl);
      setPolicyPdfUrl(null);
    }
  };

  /**
   * Capture GPS location using browser's geolocation API
   */
  const captureGPSLocation = async (): Promise<void> => {
    if (!navigator.geolocation) {
      Alert.alert(
        'Location Not Available',
        'Geolocation is not supported by your browser. You can skip this step and add location later via Manage Shop.'
      );
      return;
    }

    setLoadingLocation(true);
    console.log('📍 [SignupScreen] Requesting GPS location...');

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000, // Increased timeout to 15 seconds
          maximumAge: 60000, // Allow cached position up to 1 minute old
        });
      });

      const { latitude, longitude } = position.coords;
      console.log('✅ [SignupScreen] GPS location captured:', { latitude, longitude });

      setForm((prev) => ({
        ...prev,
        gpsLocation: {
          type: 'Point',
          coordinates: [longitude, latitude], // GeoJSON format: [longitude, latitude]
        },
      }));

      Alert.alert(
        'Success',
        `Location captured successfully!\nLatitude: ${latitude.toFixed(6)}\nLongitude: ${longitude.toFixed(6)}`
      );
    } catch (err) {
      console.error('❌ [SignupScreen] Failed to capture GPS location:', err);
      let errorMessage = 'Unable to capture location.';
      let errorTitle = 'Location Capture Failed';

      if (err instanceof GeolocationPositionError) {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorTitle = 'Permission Denied';
            errorMessage = 'Location access was denied. You can enable it in browser settings or skip this step and add location later.';
            break;
          case err.POSITION_UNAVAILABLE:
            errorTitle = 'Location Unavailable';
            errorMessage = 'Location information is currently unavailable. You can skip this step and add location later via Manage Shop.';
            break;
          case err.TIMEOUT:
            errorTitle = 'Request Timeout';
            errorMessage = 'Location request timed out. You can try again or skip this step and add location later.';
            break;
          default:
            errorMessage = 'An error occurred while capturing location. You can skip this step and add location later.';
        }
      }

      Alert.alert(errorTitle, errorMessage);
    } finally {
      setLoadingLocation(false);
    }
  };

  /**
   * Parse backend error and return user-friendly message
   */
  const parseErrorMessage = (errorMessage: string): string => {
    if (!errorMessage) {
      return 'Failed to create shop. Please try again.';
    }

    const lowerError = errorMessage.toLowerCase();

    // Extract specific field information if present (e.g., "mobile number (918087047070)")
    const extractFieldValue = (fieldName: string): string | null => {
      const regex = new RegExp(`${fieldName}\\s*\\(([^)]+)\\)`, 'i');
      const match = errorMessage.match(regex);
      return match ? match[1] : null;
    };

    // Check for backend's structured error format
    // Example: "The following field(s) are already registered: mobile number (918087047070)"
    if (lowerError.includes('already registered') || lowerError.includes('already exists')) {

      // Check for mobile number in error
      if (lowerError.includes('mobile number') || lowerError.includes('mobile_number')) {
        const mobileValue = extractFieldValue('mobile number') || extractFieldValue('mobile_number') || form.mobileNumber;
        return `The mobile number "${mobileValue}" is already registered. Please use a different number or try logging in.`;
      }

      // Check for email in error
      if (lowerError.includes('email')) {
        const emailValue = extractFieldValue('email') || form.email;
        return `The email "${emailValue}" is already registered. Please use a different email or try logging in.`;
      }

      // Check for phone (alternative wording)
      if (lowerError.includes('phone')) {
        const phoneValue = extractFieldValue('phone') || form.mobileNumber;
        return `The phone number "${phoneValue}" is already registered. Please use a different number or try logging in.`;
      }

      // Generic case - return cleaned message
      return errorMessage.replace(/The following field\(s\) are/i, 'The following fields are');
    }

    // Check for duplicate email errors (standalone)
    if (
      lowerError.includes('email') &&
      (lowerError.includes('duplicate') ||
        lowerError.includes('taken') ||
        lowerError.includes('exists'))
    ) {
      return `The email "${form.email}" is already registered. Please use a different email or try logging in.`;
    }

    // Check for duplicate mobile number errors (standalone)
    if (
      lowerError.includes('mobile') &&
      (lowerError.includes('duplicate') ||
        lowerError.includes('taken') ||
        lowerError.includes('exists'))
    ) {
      return `The mobile number "${form.mobileNumber}" is already registered. Please use a different number or try logging in.`;
    }

    // Check for duplicate phone number errors (standalone)
    if (
      lowerError.includes('phone') &&
      (lowerError.includes('duplicate') ||
        lowerError.includes('taken') ||
        lowerError.includes('exists'))
    ) {
      return `The phone number "${form.mobileNumber}" is already registered. Please use a different number or try logging in.`;
    }

    // Check for general duplicate errors
    if (lowerError.includes('duplicate data error')) {
      return 'This information is already registered. Please check your email and mobile number, or try logging in.';
    }

    // Check for validation errors
    if (lowerError.includes('invalid email')) {
      return 'Please enter a valid email address.';
    }

    if (lowerError.includes('invalid mobile') || lowerError.includes('invalid phone')) {
      return 'Please enter a valid mobile number.';
    }

    if (lowerError.includes('password')) {
      if (lowerError.includes('too short') || lowerError.includes('at least')) {
        return 'Password must be at least 8 characters long.';
      }
      return 'Please check your password requirements.';
    }

    // If message is already user-friendly (contains "Please"), return as-is
    if (errorMessage.includes('Please')) {
      return errorMessage;
    }

    // Return cleaned original message
    return errorMessage;
  };

  const handleSignup = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    // Ensure all policies are accepted
    if (!acceptTerms || !acceptPrivacy || !acceptGrievance) {
      Alert.alert('Error', 'Please accept all policies to proceed');
      return;
    }

    try {
      console.log('🚀 [SignupScreen] Starting signup process...');

      // Create shop with tncAccepted flag
      const shopData = {
        name: form.shopName,
        owner: form.ownerName,
        address: form.address,
        email: form.email,
        mobileCountryCode: form.mobileCountryCode,
        mobileNumber: form.mobileNumber,
        shopType: form.shopType,
        password: form.password,
        tncAccepted: true, // User has accepted all policies
        ...(form.gpsLocation && { gpsLocation: form.gpsLocation }), // Include GPS location if captured
        ...(form.partnerId && form.partnerId.trim() && { partnerId: form.partnerId.trim() }), // Include partner ID if provided
      };

      console.log('📝 [SignupScreen] Shop data prepared:', {
        name: shopData.name,
        owner: shopData.owner,
        address: shopData.address,
        email: shopData.email,
        shopType: shopData.shopType,
        tncAccepted: shopData.tncAccepted,
        hasGpsLocation: !!form.gpsLocation,
        hasPartnerId: !!(form.partnerId && form.partnerId.trim()),
      });

      await signup(shopData);

      // Get the created shop data from store to get shopId
      const { shop } = useAuthStore.getState();
      const createdShopId = shop?.id;

      if (!createdShopId) {
        console.error('❌ [SignupScreen] Shop created but shopId not available');
        throw new Error('Failed to get shop ID after creation');
      }

      console.log('✅ [SignupScreen] Shop created successfully');
      console.log('📋 [SignupScreen] Shop ID:', createdShopId);
      console.log('📋 [SignupScreen] Recording policy acceptance...');

      // Record policy acceptance for all three policies
      const acceptancePromises: Array<{
        type: string;
        promise: Promise<void>;
      }> = [];

      if (termsPolicy) {
        console.log('📄 [SignupScreen] Preparing Terms policy acceptance:', {
          shopId: createdShopId,
          policyId: termsPolicy.id,
          acceptedByUserId: form.email,
        });
        acceptancePromises.push({
          type: 'Terms',
          promise: policyService.acceptPolicy(createdShopId, termsPolicy.id, form.email),
        });
      }

      if (privacyPolicy) {
        console.log('📄 [SignupScreen] Preparing Privacy policy acceptance:', {
          shopId: createdShopId,
          policyId: privacyPolicy.id,
          acceptedByUserId: form.email,
        });
        acceptancePromises.push({
          type: 'Privacy',
          promise: policyService.acceptPolicy(createdShopId, privacyPolicy.id, form.email),
        });
      }

      if (grievancePolicy) {
        console.log('📄 [SignupScreen] Preparing Grievance policy acceptance:', {
          shopId: createdShopId,
          policyId: grievancePolicy.id,
          acceptedByUserId: form.email,
        });
        acceptancePromises.push({
          type: 'Grievance',
          promise: policyService.acceptPolicy(createdShopId, grievancePolicy.id, form.email),
        });
      }

      // Record all policy acceptances in parallel
      if (acceptancePromises.length > 0) {
        console.log(`🔄 [SignupScreen] Recording ${acceptancePromises.length} policy acceptances...`);

        try {
          await Promise.all(acceptancePromises.map(a => a.promise));
          console.log('✅ [SignupScreen] All policy acceptances recorded successfully');
        } catch (policyError) {
          console.error('⚠️ [SignupScreen] Policy acceptance failed but shop was created');
          console.error('🔍 [SignupScreen] Policy error details:', policyError);

          // Shop was created successfully, but policy acceptance failed
          // This is likely a backend issue, so we'll notify user but still allow login
          Alert.alert(
            'Shop Created',
            'Your shop was created successfully, but there was an issue recording policy acceptance. You can still proceed to login. Our team will follow up on this.',
            [
              {
                text: 'Login Now',
                onPress: () => {
                  if (onNavigateToLogin) {
                    onNavigateToLogin();
                  }
                },
              },
            ],
            { cancelable: false }
          );

          // Auto-navigate after a moment
          setTimeout(() => {
            if (onNavigateToLogin) {
              onNavigateToLogin();
            }
          }, 3000);

          return; // Exit early since we've handled the partial success
        }
      }

      console.log('🎉 [SignupScreen] Signup complete! Navigating to login...');

      // Show success alert and navigate to login
      Alert.alert(
        'Success!',
        'Your shop has been created successfully. Please login with your credentials.',
        [
          {
            text: 'Login Now',
            onPress: () => {
              if (onNavigateToLogin) {
                onNavigateToLogin();
              }
            },
          },
        ],
        { cancelable: false }
      );

      // Auto-navigate after a brief moment
      setTimeout(() => {
        if (onNavigateToLogin) {
          onNavigateToLogin();
        }
      }, 2000);
    } catch (error) {
      // Error is handled by the store, but we can provide additional context
      console.log('❌ [SignupScreen] Signup failed:', error);
    }
  };

  // Get selected values for display
  const selectedCountry = countries.find((c) => c.mobileCountryCode === form.mobileCountryCode);
  const selectedShopType = shopTypes.find((st) => st.code === form.shopType);

  // Loading screen - wait for both configs and policies
  if (loadingConfigs || policiesLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>
          {loadingConfigs ? 'Loading configuration...' : 'Loading policies...'}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.darkBackground}>
      <ScrollView
        contentContainerStyle={styles.container}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.modalCard}>
          {/* Logo Section */}
          <TouchableOpacity
            onPress={handleLogoClick}
            style={styles.logoContainer}
            activeOpacity={0.8}
          >
            {!logoError ? (
              <Image
                source={{ uri: logoUrl }}
                style={styles.logo}
                resizeMode="contain"
                onError={(e) => {
                  console.error('❌ [SignupScreen] Failed to load logo:', e.nativeEvent.error);
                  setLogoError(true);
                }}
              />
            ) : (
              <View style={styles.logoFallback}>
                <Text variant="h1" bold style={styles.logoFallbackText}>Bazly</Text>
              </View>
            )}
          </TouchableOpacity>

          <View style={styles.headerSection}>
            <Text variant="h1" bold style={styles.title}>
              Create Shop
            </Text>
            <Text variant="body" style={styles.subtitle}>
              {!isMobileVerified
                ? 'First, verify your mobile number and email to get started'
                : 'Complete your shop registration'
              }
            </Text>
          </View>

          <View style={styles.formSection}>
        {/* Invisible Captcha - shown on page load before verification */}
        {showCaptcha && !captchaVerified && (
          <View style={styles.pageLoadCaptchaContainer}>
            <Text style={styles.captchaLabel}>
              {captchaValidating ? '🔐 Validating with server...' : '🔐 Verifying you\'re human...'}
            </Text>
            {captchaValidating && (
              <View style={styles.loadingRow}>
                <ActivityIndicator size="small" color={colors.primary} />
                <Text style={styles.loadingText}>Validating captcha...</Text>
              </View>
            )}
            <TurnstileCaptcha
              siteKey={getConfig().turnstileSiteKey}
              onVerify={handleCaptchaVerify}
              onError={() => {
                console.error('🚫 [SignupScreen] Captcha widget failed');
                Alert.alert(
                  'Verification Error',
                  'Captcha verification failed. Please refresh the page and try again.',
                  [{
                    text: 'Refresh Page',
                    onPress: () => window.location.reload()
                  }]
                );
              }}
              theme="auto"
              size="flexible"
            />
            <Text style={styles.captchaHelpText}>
              This may take a few seconds to complete automatically
            </Text>
          </View>
        )}

        {/* Step 1: Mobile Number and Email Verification - Shown after captcha verification */}
        {captchaVerified && (
          <View style={styles.stepContainer}>
            <View style={styles.stepHeader}>
              <Text variant="h3" style={styles.stepTitle}>
                {isMobileVerified ? '✓ Step 1: Mobile & Email Verification' : 'Step 1: Verify Mobile & Email'}
              </Text>
              {isMobileVerified && (
                <Text style={styles.stepComplete}>Completed</Text>
              )}
            </View>

            {/* Email Input */}
            <Input
              label="Email *"
              placeholder="shop@example.com"
              value={form.email}
              onChangeText={handleEmailChange}
              keyboardType="email-address"
              error={errors.email}
              testID="signup-email-input"
              editable={!isMobileVerified}
            />

            {/* Show helper message for already registered email */}
            {emailAlreadyRegistered && (
              <View style={styles.emailErrorContainer}>
                <Text style={styles.emailErrorText}>
                  ⚠️ This email is already registered. Please use a different email or{' '}
                  <Text
                    style={styles.loginLinkText}
                    onPress={() => onNavigateToLogin && onNavigateToLogin()}
                  >
                    login instead
                  </Text>
                  .
                </Text>
              </View>
            )}

            {/* Combined Country Code and Mobile Number with Send OTP Button */}
            <View style={styles.fieldContainer}>
              <View style={styles.mobileNumberHeader}>
                <Text style={styles.label}>Mobile Number *</Text>
                {isMobileVerified && (
                  <View style={styles.verifiedBadge}>
                    <Text style={styles.verifiedText}>✓ Verified</Text>
                  </View>
                )}
              </View>
              <View style={styles.phoneNumberRow}>
                <TouchableOpacity
                  style={styles.countryCodeDropdown}
                  onPress={() => setShowCountryModal(true)}
                  testID="signup-countrycode-dropdown"
                  disabled={isMobileVerified}
                >
                  <Text style={styles.countryCodeText}>
                    +{selectedCountry?.mobileCountryCode || '91'}
                  </Text>
                  <Text style={styles.dropdownIcon}>▼</Text>
                </TouchableOpacity>
                <View style={styles.mobileInputContainer}>
                  <Input
                    placeholder="9876543210"
                    value={form.mobileNumber}
                    onChangeText={handleMobileNumberChange}
                    keyboardType="phone-pad"
                    maxLength={10}
                    error={errors.mobileNumber}
                    testID="signup-mobilenumber-input"
                    editable={!isMobileVerified}
                  />
                </View>
              </View>
              {errors.mobileNumber && (
                <Text style={styles.errorText}>{errors.mobileNumber}</Text>
              )}

              {/* Send OTP Button - shown when mobile number has 10 digits and email is valid */}
              {!isMobileVerified && !otpSent && form.mobileNumber.length === 10 && form.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && (
                <View style={styles.sendOtpButtonContainer}>
                  <Button
                    variant="primary"
                    onPress={handleSendOtp}
                    disabled={sendingOtp}
                    style={styles.sendOtpButton}
                    testID="send-otp-button"
                  >
                    {sendingOtp ? 'Sending OTP...' : 'Send OTP'}
                  </Button>
                </View>
              )}
            </View>

            {/* Show helper message for already registered mobile number */}
            {mobileAlreadyRegistered && (
              <View style={styles.mobileErrorContainer}>
                <Text style={styles.mobileErrorText}>
                  ⚠️ This mobile number is already registered. Please use a different mobile number or{' '}
                  <Text
                    style={styles.loginLinkText}
                    onPress={() => onNavigateToLogin && onNavigateToLogin()}
                  >
                    login instead
                  </Text>
                  .
                </Text>
              </View>
            )}

            {/* OTP Input - shown after OTP is sent */}
            {otpSent && !isMobileVerified && (
              <View style={styles.otpContainer}>
                <Text style={styles.otpLabel}>Enter 6-digit OTP sent to your mobile</Text>
                <View style={styles.otpInputRow}>
                  <Input
                    placeholder="000000"
                    value={otp}
                    onChangeText={(value) => {
                      const digitsOnly = value.replace(/\D/g, '');
                      setOtp(digitsOnly);
                      setOtpError(null);
                    }}
                    keyboardType="number-pad"
                    maxLength={6}
                    testID="otp-input"
                    style={styles.otpInput}
                  />
                  <Button
                    variant="primary"
                    onPress={handleValidateOtp}
                    disabled={verifyingOtp || otp.length !== 6}
                    style={styles.verifyButton}
                  >
                    {verifyingOtp ? 'Verifying...' : 'Verify'}
                  </Button>
                </View>
                {otpError && (
                  <Text style={styles.errorText}>{otpError}</Text>
                )}
                <TouchableOpacity
                  onPress={handleResendOtp}
                  disabled={sendingOtp}
                  style={styles.resendButton}
                >
                  <Text style={styles.resendText}>
                    {sendingOtp ? 'Sending...' : 'Resend OTP'}
                  </Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}

        {/* Step 2: Shop Details - Only shown after mobile verification */}
        {isMobileVerified && (
          <>
            <View style={styles.stepDivider} />

            <View style={styles.stepContainer}>
              <View style={styles.stepHeader}>
                <Text variant="h3" style={styles.stepTitle}>
                  Step 2: Shop Information
                </Text>
              </View>


              <Input
                label="Shop Name"
                placeholder="e.g., Pizza Palace"
                value={form.shopName}
                onChangeText={(value) => updateForm('shopName', value)}
                error={errors.shopName}
                testID="signup-shopname-input"
              />

              <Input
                label="Owner Name"
                placeholder="Your full name"
                value={form.ownerName}
                onChangeText={(value) => updateForm('ownerName', value)}
                error={errors.ownerName}
                testID="signup-ownername-input"
              />

              {/* Shop Type Dropdown */}
              <View style={styles.fieldContainer}>
                <Text style={styles.label}>Shop Type *</Text>
                <TouchableOpacity
                  style={[styles.dropdown, errors.shopType && styles.dropdownError]}
                  onPress={() => setShowShopTypeModal(true)}
                  testID="signup-shoptype-dropdown"
                >
                  <Text style={styles.dropdownText}>
                    {selectedShopType ? selectedShopType.name : 'Select Shop Type'}
                  </Text>
                  <Text style={styles.dropdownIcon}>▼</Text>
                </TouchableOpacity>
                {errors.shopType && (
                  <Text style={styles.errorText}>{errors.shopType}</Text>
                )}
              </View>

              <Input
                label="Address"
                placeholder="Shop address, landmark, area, city"
                value={form.address}
                onChangeText={(value) => updateForm('address', value)}
                multiline
                numberOfLines={3}
                testID="signup-address-input"
              />

              {/* Shop Location - Optional GPS Capture */}
              <View style={styles.shopLocationContainer}>
                <TouchableOpacity
                  style={styles.shopLocationRow}
                  onPress={captureGPSLocation}
                  disabled={loadingLocation}
                  activeOpacity={0.7}
                  testID="shop-location-checkbox"
                  accessible={true}
                  accessibilityRole="button"
                  accessibilityLabel="Capture Shop Location"
                  accessibilityHint="Tap to capture your shop's GPS location"
                >
                  <View style={[styles.compactCheckbox, form.gpsLocation && styles.checkboxChecked]}>
                    {form.gpsLocation && <Text style={styles.checkmark}>✓</Text>}
                  </View>
                  <View style={styles.shopLocationLabelContainer}>
                    <Text style={styles.shopLocationLabelText}>Shop Location (Optional)</Text>
                    <View style={styles.infoIconContainer}>
                      <Text style={styles.infoIconHover} title="Optional: Capture GPS location for better customer reach. You can update this anytime via Manage My Shop screen.">
                        ℹ️
                      </Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {loadingLocation && (
                  <View style={styles.locationLoadingRow}>
                    <ActivityIndicator size="small" color={colors.primary} />
                    <Text style={styles.locationLoadingText}>Capturing...</Text>
                  </View>
                )}

                {form.gpsLocation && (
                  <Text style={styles.locationCapturedText}>
                    ✓ {form.gpsLocation.coordinates[1].toFixed(4)}, {form.gpsLocation.coordinates[0].toFixed(4)}
                  </Text>
                )}

                {/* Helper text */}
                {!form.gpsLocation && !loadingLocation && (
                  <Text style={styles.locationHelperText}>
                    📍 Tap above to capture location (you can skip and add later)
                  </Text>
                )}
              </View>

              <Input
                label="Partner ID (Optional)"
                placeholder="e.g., E00000000001"
                value={form.partnerId}
                onChangeText={(value) => updateForm('partnerId', value)}
                testID="signup-partnerid-input"
              />

              <Input
                label="Password"
                placeholder="At least 8 characters"
                value={form.password}
                onChangeText={(value) => updateForm('password', value)}
                secureTextEntry
                error={errors.password}
                testID="signup-password-input"
              />

              <Input
                label="Confirm Password"
                placeholder="Re-enter your password"
                value={form.confirmPassword}
                onChangeText={(value) => updateForm('confirmPassword', value)}
                secureTextEntry
                error={errors.confirmPassword}
                testID="signup-confirmpassword-input"
              />
            </View>

            <View style={styles.stepDivider} />

            {/* Step 3: Policy Acceptance */}
            <View style={styles.stepContainer}>
              <View style={styles.stepHeader}>
                <Text variant="h3" style={styles.stepTitle}>
                  Step 3: Accept Policies
                </Text>
              </View>

              {/* Compact Policy Acceptance - Mobile First */}
              <View style={styles.compactPolicySection}>
                <TouchableOpacity
                  style={styles.compactCheckboxRow}
                  onPress={() => {
                    const newValue = !(acceptTerms && acceptPrivacy && acceptGrievance);
                    setAcceptTerms(newValue);
                    setAcceptPrivacy(newValue);
                    setAcceptGrievance(newValue);
                  }}
                  testID="accept-all-policies"
                >
                  <View style={[styles.compactCheckbox, (acceptTerms && acceptPrivacy && acceptGrievance) && styles.checkboxChecked]}>
                    {(acceptTerms && acceptPrivacy && acceptGrievance) && <Text style={styles.checkmark}>✓</Text>}
                  </View>
                  <Text style={styles.compactPolicyText}>
                    I accept{' '}
                    <Text style={styles.policyLink} onPress={(e) => { e?.stopPropagation(); handleViewPolicy('TERMS'); }}>
                      Terms
                    </Text>
                    {', '}
                    <Text style={styles.policyLink} onPress={(e) => { e?.stopPropagation(); handleViewPolicy('PRIVACY'); }}>
                      Privacy
                    </Text>
                    {' & '}
                    <Text style={styles.policyLink} onPress={(e) => { e?.stopPropagation(); handleViewPolicy('GRIEVANCE'); }}>
                      Grievance
                    </Text>
                    {' *'}
                  </Text>
                </TouchableOpacity>
              </View>

              <Button
                onPress={handleSignup}
                disabled={!canSubmit()}
                loading={isLoading}
                style={styles.signupButton}
                testID="signup-button"
              >
                {isLoading ? 'Creating Shop...' : 'Create Shop'}
              </Button>
            </View>
          </>
        )}
          </View>

          {error && (
            <View style={styles.errorContainer}>
              <View style={styles.errorIcon}>
                <Text style={styles.errorIconText}>⚠️</Text>
              </View>
              <Text variant="body" color="error" style={styles.errorMessage}>
                {parseErrorMessage(error)}
              </Text>
            </View>
          )}

          <View style={styles.loginSection}>
            <Text variant="body" style={styles.loginText}>
              Already have an account?
            </Text>
            <Button
              variant="ghost"
              onPress={onNavigateToLogin}
              testID="navigate-to-login"
            >
              Login Here
            </Button>
          </View>
        </View>
      </ScrollView>

      {/* Country Code Modal */}
      <Modal
        visible={showCountryModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowCountryModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text variant="h3" bold>
                Select Country Code
              </Text>
              <TouchableOpacity
                onPress={() => setShowCountryModal(false)}
                style={styles.closeButton}
              >
                <Text style={styles.closeButtonText}>✕</Text>
              </TouchableOpacity>
            </View>
            <FlatList
              data={countries}
              keyExtractor={(item) => item.id}
              style={styles.modalList}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.modalItem}
                  onPress={() => {
                    updateForm('mobileCountryCode', item.mobileCountryCode);
                    setShowCountryModal(false);
                  }}
                >
                  <Text style={styles.modalItemText}>
                    +{item.mobileCountryCode}
                  </Text>
                  <Text style={styles.modalItemSubtext}>
                    {item.countryName} ({item.currency})
                  </Text>
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      {/* Shop Type Modal */}
      <Modal
        visible={showShopTypeModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowShopTypeModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text variant="h3" bold>
                Select Shop Type
              </Text>
              <TouchableOpacity
                onPress={() => setShowShopTypeModal(false)}
                style={styles.closeButton}
              >
                <Text style={styles.closeButtonText}>✕</Text>
              </TouchableOpacity>
            </View>
            <FlatList
              data={shopTypes}
              keyExtractor={(item) => item.id}
              style={styles.modalList}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.modalItem}
                  onPress={() => {
                    updateForm('shopType', item.code);
                    setShowShopTypeModal(false);
                  }}
                >
                  <Text style={styles.modalItemText}>{item.name}</Text>
                  {item.description && (
                    <Text style={styles.modalItemSubtext}>
                      {item.description}
                    </Text>
                  )}
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      {/* Policy Viewer Modal */}
      <Modal
        visible={showPolicyModal}
        transparent
        animationType="slide"
        onRequestClose={handleClosePolicyModal}
      >
        <View style={styles.policyModalOverlay}>
          <View style={styles.policyModalContent}>
            {/* Modal Header */}
            <View style={styles.policyModalHeader}>
              <Text variant="h3" bold style={styles.policyModalTitle}>
                {viewingPolicy?.type === 'Terms' ? 'Terms and Conditions' :
                 viewingPolicy?.type === 'Privacy' ? 'Privacy Policy' :
                 'Grievance Redressal Policy'}
              </Text>
              <TouchableOpacity
                onPress={handleClosePolicyModal}
                style={styles.policyCloseButton}
              >
                <Text style={styles.policyCloseButtonText}>✕</Text>
              </TouchableOpacity>
            </View>

            {/* PDF Viewer */}
            <View style={styles.policyPdfContainer}>
              {loadingPdf ? (
                <View style={styles.pdfLoadingContainer}>
                  <ActivityIndicator size="large" color={colors.primary} />
                  <Text style={styles.pdfLoadingText}>Loading document...</Text>
                </View>
              ) : policyPdfUrl ? (
                <iframe
                  src={policyPdfUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title="Policy Document"
                />
              ) : (
                <View style={styles.pdfErrorContainer}>
                  <Text style={styles.pdfErrorText}>Failed to load document</Text>
                </View>
              )}
            </View>

            {/* Footer with metadata */}
            {viewingPolicy && (
              <View style={styles.policyModalFooter}>
                <Text style={styles.policyMetaText}>
                  Version: {viewingPolicy.version} |
                  Last Updated: {new Date(viewingPolicy.createdAt).toLocaleDateString()}
                </Text>
              </View>
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: colors.white,
  },
  // Dark background container - full viewport
  darkBackground: {
    flex: 1,
    backgroundColor: '#1a1a1a',
    minHeight: '100vh',
  },
  container: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 32,
  },
  // White modal card container
  modalCard: {
    backgroundColor: colors.white,
    borderRadius: 12,
    paddingHorizontal: 32,
    paddingVertical: 40,
    width: '100%',
    maxWidth: 600,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 32,
  },
  logo: {
    width: 180,
    height: 60,
  },
  logoFallback: {
    width: 180,
    height: 60,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
  },
  logoFallbackText: {
    fontSize: 28,
    color: '#333',
  },
  headerSection: {
    marginBottom: 24,
    alignItems: 'center',
  },
  title: {
    marginBottom: 8,
    textAlign: 'center',
    color: '#1a1a1a',
    fontSize: 28,
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
    color: colors.gray7,
  },
  formSection: {
    marginBottom: 16,
  },
  // Step-based form styles
  stepContainer: {
    marginBottom: 8,
  },
  stepHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
  },
  stepTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.gray9,
  },
  stepComplete: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.success,
    backgroundColor: colors.gray1,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  stepDivider: {
    height: 1,
    backgroundColor: colors.gray3,
    marginVertical: 24,
  },
  fieldContainer: {
    marginVertical: 6,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray9,
    marginBottom: 8,
  },
  phoneNumberRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
  },
  countryCodeDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: colors.gray3,
    borderRadius: 8,
    backgroundColor: colors.white,
    minWidth: 90,
    justifyContent: 'space-between',
  },
  countryCodeText: {
    fontSize: 14,
    color: colors.gray9,
    fontWeight: '600',
    marginRight: 4,
  },
  mobileInputContainer: {
    flex: 1,
  },
  // Mobile Verification Styles
  mobileNumberHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  verifiedBadge: {
    backgroundColor: colors.success,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  verifiedText: {
    color: colors.white,
    fontSize: 12,
    fontWeight: '600',
  },
  captchaContainer: {
    marginTop: 12,
    padding: 12,
    backgroundColor: colors.gray1,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.gray3,
  },
  captchaLabel: {
    fontSize: 13,
    color: colors.gray8,
    textAlign: 'center',
    marginBottom: 8,
    fontWeight: '500',
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    gap: 8,
  },
  otpContainer: {
    marginTop: 12,
    padding: 12,
    backgroundColor: colors.gray1,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  otpLabel: {
    fontSize: 13,
    color: colors.gray8,
    marginBottom: 8,
    textAlign: 'center',
  },
  otpInputRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  otpInput: {
    flex: 1,
    textAlign: 'center',
    fontSize: 18,
    fontWeight: '600',
    letterSpacing: 2,
  },
  verifyButton: {
    minWidth: 80,
    paddingHorizontal: 12,
  },
  resendButton: {
    alignSelf: 'center',
    marginTop: 8,
    paddingVertical: 8,
  },
  resendText: {
    fontSize: 13,
    color: colors.primary,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  dropdown: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: colors.gray3,
    borderRadius: 8,
    backgroundColor: colors.white,
  },
  dropdownError: {
    borderColor: colors.error,
  },
  dropdownText: {
    fontSize: 14,
    color: colors.gray9,
    flex: 1,
  },
  dropdownIcon: {
    fontSize: 12,
    color: colors.gray6,
    marginLeft: 8,
  },
  errorText: {
    fontSize: 12,
    color: colors.error,
    marginTop: 4,
  },
  // Email already registered error styles
  emailErrorContainer: {
    backgroundColor: '#FFF3CD', // Light yellow background
    borderLeftWidth: 4,
    borderLeftColor: '#FFC107', // Warning color
    padding: 12,
    borderRadius: 6,
    marginTop: 8,
  },
  emailErrorText: {
    fontSize: 13,
    color: '#664D03', // Dark yellow text
    lineHeight: 18,
  },
  // Mobile number already registered error styles
  mobileErrorContainer: {
    backgroundColor: '#FFF3CD', // Light yellow background
    borderLeftWidth: 4,
    borderLeftColor: '#FFC107', // Warning color
    padding: 12,
    borderRadius: 6,
    marginTop: 8,
  },
  mobileErrorText: {
    fontSize: 13,
    color: '#664D03', // Dark yellow text
    lineHeight: 18,
  },
  loginLinkText: {
    color: colors.primary,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  errorContainer: {
    backgroundColor: '#FEE2E2', // Light red background
    borderLeftWidth: 4,
    borderLeftColor: colors.error,
    padding: 16,
    borderRadius: 8,
    marginVertical: 12,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 2, // For Android
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  errorIcon: {
    marginRight: 12,
    marginTop: 2,
  },
  errorIconText: {
    fontSize: 20,
  },
  errorMessage: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    color: '#991B1B',
    fontWeight: '500',
  },
  // Shop Location - Compact Mobile-First Styles
  shopLocationContainer: {
    marginVertical: 12,
  },
  shopLocationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  shopLocationLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginLeft: 12,
  },
  shopLocationLabelText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray9,
  },
  infoIconContainer: {
    marginLeft: 6,
    marginTop: -2,
  },
  infoIconHover: {
    fontSize: 14,
    color: colors.primary,
    cursor: 'pointer' as any,
  },
  locationLoadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 36,
    marginTop: 4,
  },
  locationLoadingText: {
    fontSize: 12,
    color: colors.gray6,
    marginLeft: 8,
  },
  locationCapturedText: {
    fontSize: 11,
    color: colors.success,
    marginLeft: 36,
    marginTop: 4,
    fontFamily: 'monospace',
  },
  locationHelperText: {
    fontSize: 12,
    color: colors.gray6,
    marginLeft: 36,
    marginTop: 4,
    fontStyle: 'italic',
  },
  // Compact Policy Section - Mobile First
  compactPolicySection: {
    marginVertical: 12,
    paddingVertical: 8,
  },
  compactCheckboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 8,
  },
  compactCheckbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: colors.gray4,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.white,
    marginTop: 2,
  },
  compactPolicyText: {
    fontSize: 13,
    color: colors.gray8,
    lineHeight: 20,
    marginLeft: 10,
    flex: 1,
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  checkmark: {
    color: colors.white,
    fontSize: 14,
    fontWeight: 'bold',
  },
  policyLink: {
    color: colors.primary,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  signupButton: {
    marginTop: 12,
    marginBottom: 8,
  },
  loginSection: {
    alignItems: 'center',
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  loginText: {
    marginBottom: 8,
    color: colors.gray7,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    paddingBottom: 32,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.gray2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 18,
    color: colors.gray7,
  },
  modalList: {
    paddingHorizontal: 20,
  },
  modalItem: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  modalItemText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.gray9,
    marginBottom: 4,
  },
  modalItemSubtext: {
    fontSize: 14,
    color: colors.gray6,
  },
  // Policy Viewer Modal Styles
  policyModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  policyModalContent: {
    backgroundColor: colors.white,
    borderRadius: 12,
    width: '100%',
    maxWidth: 900,
    height: '90%',
    overflow: 'hidden',
  },
  policyModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
    backgroundColor: colors.primary,
  },
  policyModalTitle: {
    color: colors.white,
    flex: 1,
  },
  policyCloseButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  policyCloseButtonText: {
    fontSize: 24,
    color: colors.white,
    fontWeight: 'bold',
  },
  policyPdfContainer: {
    flex: 1,
    backgroundColor: colors.gray1,
  },
  pdfLoadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pdfLoadingText: {
    marginTop: 16,
    fontSize: 14,
    color: colors.gray6,
  },
  pdfErrorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pdfErrorText: {
    fontSize: 16,
    color: colors.error,
  },
  policyModalFooter: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: colors.gray2,
    backgroundColor: colors.gray1,
  },
  policyMetaText: {
    fontSize: 12,
    color: colors.gray6,
    textAlign: 'center',
  },
  // Page load captcha styles
  pageLoadCaptchaContainer: {
    padding: 24,
    backgroundColor: colors.gray1,
    borderRadius: 8,
    marginBottom: 24,
    alignItems: 'center',
  },
  captchaHelpText: {
    fontSize: 12,
    color: colors.gray6,
    textAlign: 'center',
    marginTop: 8,
    fontStyle: 'italic',
  },
  // Send OTP button styles
  sendOtpButtonContainer: {
    marginTop: 12,
    marginBottom: 8,
  },
  sendOtpButton: {
    width: '100%',
  },
});

